import {
  Annotations,
  type AnnotationsProvider,
  type AnnotationKind,
  type AnnotationKey,
  type ReferenceAhaObject,
} from '@aha-app/aha-editor-v2';
import type AhaEditorFieldV2Controller from '../../controllers/AhaEditorFieldV2Controller';
import { Annotation } from 'components/framework/models';
import AhaDrawer from 'javascripts/drawer';
import { type ModelName } from 'javascripts/shared-types';

export type AnnotationEventName =
  | 'newannotation'
  | 'reorederannotations'
  | 'changeannotationhiddenstate'
  | 'cancelannotation'
  | 'selectannotations'
  | 'changeannotationresolvedstate'
  | 'changeannotationtext'
  | 'reorderannotations'
  | 'deleteannotation';

interface SelectAnnotationsOptions {
  ids: AnnotationKey[];
  scroll: boolean;
  showIfResolved: boolean;
}

// Provide the annotations API to the editor. Methods named on* are called by the editor.
// Methods named handle* are called by the sidepanel (in note_annotations.js).
class AhaAnnotationsProvider implements AnnotationsProvider {
  type = 'annotations' as const;

  eventHandlers: {
    eventName: string;
    handler: (event: JQueryEventObject) => void;
  }[] = [];

  constructor(
    private readonly controller: AhaEditorFieldV2Controller,
    private readonly objectType: string,
    private readonly documentId: string,
    private readonly createRecordLink: (
      reference: Partial<ReferenceAhaObject>,
      annotationId?: RecordId
    ) => Promise<void>
  ) {
    // Hack to wait until the sidebar is initialized.
    const initializeInterval = setInterval(() => {
      if (this.controller.$annotationsSidebar?.length) {
        this.initializeSidebarEvents();
        clearInterval(initializeInterval);
      }
    }, 5);
  }

  onDestroy(): void {
    this.unsubscribeSidebarEvents();
  }

  initializeSidebarEvents(): void {
    const sidebarElement = this.controller.$annotationsSidebar!;

    const registerEvent = (
      eventName: string,
      handler: (event: JQueryEventObject) => void
    ) => {
      sidebarElement.on(eventName, handler);
      this.eventHandlers.push({ eventName, handler });
    };

    registerEvent('createannotation', this.handleCreateAnnotation.bind(this));
    registerEvent('changeannotations', this.handleChangeAnnotations.bind(this));
    registerEvent('cancelannotation', this.handleCancelAnnotation.bind(this));
    registerEvent('selectannotations', this.handleSelectAnnotations.bind(this));
    registerEvent(
      'changeannotationresolvedstate',
      this.handleChangeAnnotationResolved.bind(this)
    );
    registerEvent('deleteannotation', this.handleDeleteAnnotation.bind(this));
  }

  unsubscribeSidebarEvents() {
    const sidebarElement = this.controller.$annotationsSidebar;
    if (!sidebarElement) return;

    for (const { eventName, handler } of this.eventHandlers) {
      // @ts-expect-error JQuery handler types are too complicated.
      sidebarElement.off(eventName, handler);
    }
  }

  handleCreateAnnotation(
    _,
    id: AnnotationKey,
    data?: { userId?: RecordId }
  ): void {
    if (!this.editor) return;

    Annotations.createAnnotation(this.editor, { key: id, ...data });
  }

  handleChangeAnnotations(): void {
    if (!this.editor) return;

    const orderedAnnotationIds = Annotations.getAnnotationKeys(this.editor);
    this.triggerAnnotationEvent('reorderannotations', [orderedAnnotationIds]);
  }

  handleCancelAnnotation() {
    if (!this.editor) return;

    Annotations.removeAnnotation(this.editor);

    return false;
  }

  handleSelectAnnotations(_, options: SelectAnnotationsOptions) {
    if (!this.editor) return;
    const { ids } = options;

    Annotations.selectAnnotation(this.editor, { key: ids[0] });

    return false;
  }

  handleChangeAnnotationResolved(_, key: AnnotationKey, resolved: boolean) {
    if (!this.editor) return;

    Annotations.changeAnnotationResolved(this.editor, { key, resolved });
  }

  handleDeleteAnnotation(_, key: AnnotationKey) {
    if (!this.editor) return;

    Annotations.removeAnnotation(this.editor, { key });
  }

  async onNewAnnotation(
    text: string,
    kind: AnnotationKind,
    optionalConfiguration: Record<string, unknown>
  ) {
    if (!this.editor) return;

    if (kind === 'comment') {
      this.controller.actionShowSidePanel({ open: true, panel: 'comments' });

      this.triggerAnnotationEvent('newannotation', {
        text,
        annotatableType: this.objectType,
        annotatableId: this.documentId,
      });
    } else if (kind === 'record') {
      const editorAnnotation = Annotations.getLocalAnnotation(this.editor);
      if (editorAnnotation?.kind !== 'record') return;

      const annotationRecord = new Annotation({
        type: 'RECORD',
        annotatable: {
          id: this.documentId,
          typename: this.objectType,
        },
        configuration: {
          text,
        },
      });

      await annotationRecord.save();

      await this.createRecordLink(
        {
          id: editorAnnotation.recordId,
          class: editorAnnotation.recordType,
        },
        annotationRecord.id
      );

      Annotations.createAnnotation(this.editor, {
        key: annotationRecord.id,
      });
    }
  }

  onAnnotationCanceled(): void {
    this.triggerAnnotationEvent('cancelannotation');
  }

  onAnnotationsSelected(ids: AnnotationKey[]): void {
    this.triggerAnnotationEvent('selectannotations', {
      ids,
      scroll: true,
      showIfResolved: false,
    });
  }

  onAnnotationClicked(key: AnnotationKey): void {
    if (!this.editor) return;

    const annotation = this.editor?.metadata?.annotations?.[key];
    if (!annotation) return;

    if (annotation.kind === 'comment') {
      if (annotation.resolved) return;

      this.controller.actionShowSidePanel({
        open: true,
        panel: 'comments',
      });
    } else if (annotation.kind === 'record') {
      if (this.controller.isReadOnly || this.controller.isLocked) {
        AhaDrawer.openRecord({
          typename: annotation.recordType as ModelName,
          id: annotation.recordId,
        });
      }
    }
  }

  onAnnotationResolved(key: AnnotationKey): void {
    this.triggerAnnotationEvent('changeannotationresolvedstate', [key, true]);
  }

  onAnnotationUnresolved(key: AnnotationKey): void {
    this.triggerAnnotationEvent('changeannotationresolvedstate', [key, false]);
  }

  onAnnotationHidden(key: AnnotationKey): void {
    const annotation = this.editor?.metadata?.annotations?.[key];
    if (!annotation || annotation.kind === 'record') return;

    this.triggerAnnotationEvent('changeannotationhiddenstate', [key, true]);
  }

  onAnnotationShown(key: AnnotationKey): void {
    this.triggerAnnotationEvent('changeannotationhiddenstate', [key, false]);
  }

  onAnnotationTextChanged(key: AnnotationKey, newText: string): void {
    if (!this.editor) return;

    const editorAnnotation = Annotations.getAnnotation(this.editor, key);
    // Comment annotation text should not change
    if (!editorAnnotation || editorAnnotation.kind === 'comment') return;

    this.triggerAnnotationEvent('changeannotationtext', [key, newText]);
  }

  onAnnotationDeleted(key: string): void {
    if (!this.editor) return;

    const annotation = Annotations.getAnnotation(this.editor, key);
    if (!annotation) return;

    if (annotation.kind === 'record') {
      const annotationRecord = new Annotation({
        id: key,
        type: 'RECORD',
      });

      annotationRecord.destroy();
    }
  }

  onShowAnnotations(): void {
    this.controller.actionShowSidePanel({ open: true, panel: 'comments' });
  }

  // Send the event to the sidepanel.
  private triggerAnnotationEvent(type: AnnotationEventName, ...args: any[]) {
    this.controller.$annotatableElement?.trigger(type, ...args);
  }

  get editor() {
    return this.controller.ahaEditor?.editor;
  }
}

export default AhaAnnotationsProvider;
